import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Eye, 
  Lock, 
  Database, 
  Users, 
  AlertTriangle,
  CheckCircle,
  FileText,
  Clock,
  Mail,
  Phone,
  Settings,
  Trash2,
  Download,
  Edit3
} from 'lucide-react';

const KVKK = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "15 Aralık 2024";

  const userRights = [
    {
      icon: <Eye className="w-5 h-5" />,
      title: "Bilgilendirme Hakkı",
      description: "Kişisel verilerinizin işlenip işlenmediğini öğrenme",
      details: "Hangi verilerinizin toplandığını ve nasıl kullanıldığını öğrenebilirsiniz."
    },
    {
      icon: <Database className="w-5 h-5" />,
      title: "Erişim Hakkı",
      description: "İşlenen kişisel verilerinize erişim talep etme",
      details: "Size ait tüm verileri indirip inceleyebilirsiniz."
    },
    {
      icon: <Edit3 className="w-5 h-5" />,
      title: "Düzeltme Hakkı",
      description: "Yanlış veya eksik verilerin düzeltilmesini isteme",
      details: "Hatalı bilgilerinizi güncelleyebilir veya düzeltebilirsiniz."
    },
    {
      icon: <Trash2 className="w-5 h-5" />,
      title: "Silme Hakkı",
      description: "Kişisel verilerinizin silinmesini talep etme",
      details: "Hesabınızı ve tüm verilerinizi kalıcı olarak silebilirsiniz."
    },
    {
      icon: <Settings className="w-5 h-5" />,
      title: "İşleme İtiraz Hakkı",
      description: "Veri işlenmesine itiraz etme ve durdurma",
      details: "Belirli veri işleme faaliyetlerine itiraz edebilirsiniz."
    },
    {
      icon: <Download className="w-5 h-5" />,
      title: "Veri Taşınabilirliği",
      description: "Verilerinizi başka platformlara taşıma",
      details: "Verilerinizi yapılandırılmış formatta alabilirsiniz."
    }
  ];

  const dataTypes = [
    {
      category: "Kimlik Bilgileri",
      items: ["Ad, soyad", "E-posta adresi", "Telefon numarası", "Kullanıcı adı"],
      purpose: "Hesap oluşturma ve kimlik doğrulama"
    },
    {
      category: "İş Bilgileri",
      items: ["Şirket adı", "Departman", "Pozisyon", "İş adresi"],
      purpose: "Hizmet kişiselleştirme ve iletişim"
    },
    {
      category: "Teknik Veriler",
      items: ["IP adresi", "Tarayıcı bilgisi", "Cihaz bilgisi", "Log kayıtları"],
      purpose: "Sistem güvenliği ve performans"
    },
    {
      category: "Kullanım Verileri",
      items: ["Giriş saatleri", "Kullanılan özellikler", "Proje verileri", "Görev bilgileri"],
      purpose: "Hizmet iyileştirme ve analiz"
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Shield className={`w-7 h-7 mr-3 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <div>
                  <h1 className="text-2xl font-bold">KVKK Aydınlatma Metni</h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Kişisel Verilerin Korunması Kanunu • Son güncelleme: {lastUpdated}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
            }`}>
              KVKK Uyumlu
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-start">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/30' : 'bg-green-100'} mr-4`}>
                <Shield className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Veri Sorumlusu: Beetrax Teknoloji A.Ş.</h2>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed mb-4`}>
                  6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında, 
                  kişisel verilerinizin işlenmesine ilişkin aydınlatma yükümlülüğümüzü yerine getirmek amacıyla 
                  hazırlanmış aydınlatma metnidir.
                </p>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
                  <p className="text-sm">
                    <strong>Adres:</strong> Muallimköy, Deniz Cd. No:143-5, 41400 Gebze/Kocaeli
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Data Types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-center mb-6">
              <Database className={`w-6 h-6 mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <h2 className="text-xl font-bold">İşlenen Kişisel Veri Kategorileri</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dataTypes.map((dataType, index) => (
                <div key={index} className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                  <h3 className="font-semibold mb-3 text-lg">{dataType.category}</h3>
                  <ul className="space-y-1 mb-3">
                    {dataType.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className={`w-3 h-3 mr-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <strong>Amaç:</strong> {dataType.purpose}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Processing Purposes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-center mb-6">
              <Settings className={`w-6 h-6 mr-3 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <h2 className="text-xl font-bold">Kişisel Verilerin İşlenme Amaçları</h2>
            </div>

            <div className="space-y-4">
              {[
                "Hizmet sunumunu gerçekleştirmek ve geliştirmek",
                "Kullanıcı hesaplarını yönetmek ve güvenliğini sağlamak", 
                "Müşteri destek hizmetleri sunmak",
                "Yasal yükümlülükleri yerine getirmek",
                "İstatistiksel analiz ve araştırma yapmak",
                "Pazarlama ve iletişim faaliyetleri yürütmek",
                "Sistem güvenliğini sağlamak ve siber saldırıları önlemek",
                "Hizmet kalitesini ölçmek ve iyileştirmek"
              ].map((purpose, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <p>{purpose}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* User Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-center mb-6">
              <Users className={`w-6 h-6 mr-3 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
              <h2 className="text-xl font-bold">KVKK Kapsamındaki Haklarınız</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {userRights.map((right, index) => (
                <div key={index} className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className="flex items-start mb-3">
                    <span className={`mr-3 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                      {right.icon}
                    </span>
                    <div>
                      <h3 className="font-semibold mb-1">{right.title}</h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                        {right.description}
                      </p>
                      <p className="text-xs">{right.details}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
              <div className="flex items-start">
                <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h4 className={`font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'} mb-1`}>
                    Hak Kullanımı
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    Yukarıdaki haklarınızı kullanmak için info@beetrax.net adresine başvurabilir 
                    veya platform üzerindeki hesap ayarlarından yönetebilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Data Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-center mb-6">
              <Lock className={`w-6 h-6 mr-3 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              <h2 className="text-xl font-bold">Veri Güvenliği ve Saklama</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
                <h3 className="font-semibold mb-2">🔐 Teknik Güvenlik</h3>
                <ul className="text-sm space-y-1">
                  <li>• 256-bit SSL şifreleme</li>
                  <li>• Güvenlik duvarları</li>
                  <li>• Düzenli güvenlik testleri</li>
                  <li>• Erişim kontrolü</li>
                </ul>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
                <h3 className="font-semibold mb-2">📋 İdari Güvenlik</h3>
                <ul className="text-sm space-y-1">
                  <li>• Personel eğitimleri</li>
                  <li>• Erişim yetkilendirme</li>
                  <li>• İç denetim süreçleri</li>
                  <li>• Gizlilik sözleşmeleri</li>
                </ul>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'} border ${isDark ? 'border-purple-700' : 'border-purple-200'}`}>
                <h3 className="font-semibold mb-2">⏳ Saklama Süreleri</h3>
                <ul className="text-sm space-y-1">
                  <li>• Hesap verileri: Aktif süre</li>
                  <li>• Log kayıtları: 2 yıl</li>
                  <li>• Yasal belgeler: 10 yıl</li>
                  <li>• Pazarlama: İzin süresi</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-center mb-6">
              <Mail className={`w-6 h-6 mr-3 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <h2 className="text-xl font-bold">İletişim ve Başvuru</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center mb-3">
                  <Mail className="w-5 h-5 mr-2" />
                  <h3 className="font-semibold">KVKK Başvuruları</h3>
                </div>
                <p className="text-sm mb-2">info@beetrax.net</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Veri sahibi hakları için özel e-posta adresi
                </p>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center mb-3">
                  <Phone className="w-5 h-5 mr-2" />
                  <h3 className="font-semibold">Destek Hattı</h3>
                </div>
                <p className="text-sm mb-2">+90 (552) 424 11 08</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Hafta içi 09:00-18:00 arası
                </p>
              </div>
            </div>

            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'} border ${isDark ? 'border-yellow-700' : 'border-yellow-200'}`}>
              <div className="flex items-start">
                <Clock className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h4 className={`font-medium ${isDark ? 'text-yellow-200' : 'text-yellow-800'} mb-1`}>
                    Başvuru Süreci
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                    KVKK başvurularınız en geç 30 gün içinde değerlendirilir ve yanıtlanır. 
                    Başvurularınızda kimlik teyidi gerekebilir.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border text-center`}
          >
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Bu aydınlatma metni {lastUpdated} tarihinde güncellenmiştir. 
              KVKK ile ilgili sorularınız için info@beetrax.net adresine yazabilirsiniz.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/privacy-policy')}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                } transition-colors`}
              >
                Gizlilik Politikası
              </button>
              <button
                onClick={() => navigate('/terms-of-service')}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                } transition-colors`}
              >
                Kullanım Şartları
              </button>
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-2 rounded-lg ${
                  isDark 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                } transition-colors`}
              >
                Ana Sayfaya Dön
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default KVKK; 